import React, { useState } from 'react';
import { BiTrash } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';

import { deleteAccount } from '../../shared/api/user';
import { ASK_AI_HIGHLIGHT_KEY, ONBOARDING_DONE_KEY } from '../OnboardingFlow';
import { Modal } from '../ui/Modal';

const DangerZone: React.FC = () => {
  const navigate = useNavigate();
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [deleteUserAccount, setDeleteUserAccount] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isDeleteSuccess, setIsDeleteSuccess] = useState(false);

  const handleOpenDeletePopup = () => {
    setDeleteUserAccount(false);
    setIsDeleteSuccess(false);
    setIsDeletePopupOpen(true);
  };

  const handleDeleteAccount = async () => {
    setIsDeleteLoading(true);
    try {
      const response = await deleteAccount(deleteUserAccount);
      if (!response || !response.success) {
        throw new Error('Failed to delete account');
      }
      setIsDeleteLoading(false);
      setIsDeleteSuccess(true);
      localStorage.removeItem(ONBOARDING_DONE_KEY);
      localStorage.removeItem(ASK_AI_HIGHLIGHT_KEY);
      setTimeout(() => {
        setIsDeletePopupOpen(false);
        navigate('/logout');
      }, 3000);
    } catch (error) {
      setIsDeleteLoading(false);
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
    }
  };

  return (
    <div className="card bg-error/5 shadow-card">
      <div className="card-body p-4 gap-3">
        <div>
          <div className="font-semibold text-error">Danger zone</div>
          <div className="text-sm opacity-70 max-w-lg">
            Permanently delete your synced data, or your data and account together. This cannot be undone.
          </div>
        </div>
        <div>
          <button className="btn btn-outline btn-error btn-sm" onClick={handleOpenDeletePopup}>
            <BiTrash className="text-base" />
            Delete account
          </button>
        </div>
      </div>

      {isDeletePopupOpen && (
        <Modal onClose={() => setIsDeletePopupOpen(false)} className="max-w-md" labelledBy="delete-account-title">
          <div className="p-6">
            {!isDeleteLoading && !isDeleteSuccess && (
              <>
                <h2 id="delete-account-title" className="text-xl font-bold mb-4">
                  Delete Account Data
                </h2>
                <p className="text-base-content/70 mb-5">
                  <span className="text-error font-semibold">Warning:</span> This will permanently remove all your data.
                  This action cannot be undone.
                </p>

                <label className="flex items-start gap-3 cursor-pointer mb-6 rounded-lg bg-base-200 hover:bg-base-300/60 transition-colors p-3">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-error mt-0.5 shrink-0"
                    checked={deleteUserAccount}
                    onChange={(e) => setDeleteUserAccount(e.target.checked)}
                  />
                  <span className="text-sm leading-snug">
                    Also delete my account
                    <span className="block text-xs opacity-60 mt-0.5">
                      You will not be able to sign in with this account again
                    </span>
                  </span>
                </label>

                <div className="flex justify-end gap-3">
                  <button className="btn btn-ghost btn-sm" onClick={() => setIsDeletePopupOpen(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-error btn-sm" onClick={handleDeleteAccount}>
                    {deleteUserAccount ? 'Delete Data & Account' : 'Delete Data'}
                  </button>
                </div>
              </>
            )}

            {isDeleteLoading && (
              <div className="flex flex-col items-center gap-4 py-4">
                <span className="loading loading-spinner loading-lg text-error" />
                <p className="text-base-content/70">Deleting your data...</p>
              </div>
            )}

            {isDeleteSuccess && (
              <div className="text-center py-2">
                <h2 className="text-xl font-bold text-success mb-3">
                  {deleteUserAccount ? 'Account Deleted' : 'Data Deleted'}
                </h2>
                <p className="text-base-content/70">
                  {deleteUserAccount
                    ? 'Your account and all data have been permanently deleted. You will be redirected shortly.'
                    : 'All your data has been permanently deleted. You will be redirected shortly.'}
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DangerZone;
